import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  Camera,
  CheckCircle2,
  ClipboardList,
  Clock,
  ImageIcon,
  Loader2,
  MapPin,
  MessageSquare,
  Phone,
  Save,
  User,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { useGraphQLMutation } from '../hooks/useGraphQLMutation.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type OrderStatus = 'pending' | 'in-progress' | 'completed' | 'cancelled';

/* ── GraphQL query ── */

const GET_SERVICE_ORDER = `
  query GetServiceOrder($accessKey: Float!) {
    serviceOrder(accessKey: $accessKey) {
      accessKey
      ticketno
      description
      notes
      status
      priority
      assignedTo
      clientCode
      createdDate
      dueDate
      completedDate
      client {
        clientCode
        clientName
        contact
        address
        city
        state
        zip
        phone
        email
      }
      units {
        accessKey
        unitNo
        floor
        serviceComplete
        accessDenied
      }
      timeRecords {
        id
        timeStart
        timeStop
        timeResume
        cummulative
        status
        notes
        timeLocation {
          clockInTime
          clockOutTime
          clockInLatitude
          clockInLongitude
          clockInAddress
          clockInCity
          clockInState
          clockOutAddress
          clockOutCity
          clockOutState
        }
      }
      images {
        id
        cfImageId
        thumbnailUrl
        filename
        contentType
        fileSize
        width
        height
        uploadedBy
        uploadedDate
      }
    }
  }
`;

/* ── GraphQL response shape ── */

interface ServiceOrderUnitNode {
  accessKey: number;
  unitNo: string;
  floor: string | null;
  serviceComplete: boolean;
  accessDenied: boolean;
}

interface TimeLocationNode {
  clockInTime: string | null;
  clockOutTime: string | null;
  clockInLatitude: number | null;
  clockInLongitude: number | null;
  clockInAddress: string | null;
  clockInCity: string | null;
  clockInState: string | null;
  clockOutAddress: string | null;
  clockOutCity: string | null;
  clockOutState: string | null;
}

interface ServiceOrderTimeNode {
  id: number;
  timeStart: string;
  timeStop: string | null;
  timeResume: string | null;
  cummulative: number | null;
  status: string | null;
  notes: string | null;
  timeLocation: TimeLocationNode | null;
}

interface ServiceOrderImageNode {
  id: number;
  cfImageId: string | null;
  thumbnailUrl: string | null;
  filename: string;
  contentType: string | null;
  fileSize: number | null;
  width: number | null;
  height: number | null;
  uploadedBy: string | null;
  uploadedDate: string;
}

interface ServiceOrderDetailNode {
  accessKey: number;
  ticketno: string;
  description: string;
  notes: string | null;
  status: string;
  priority: string;
  assignedTo: string;
  clientCode: string;
  createdDate: string;
  dueDate: string | null;
  completedDate: string | null;
  client: {
    clientCode: string;
    clientName: string;
    contact: string | null;
    address: string | null;
    city: string;
    state: string;
    zip: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  units: ServiceOrderUnitNode[];
  timeRecords: ServiceOrderTimeNode[];
  images: ServiceOrderImageNode[];
}

interface ServiceOrderData {
  serviceOrder: ServiceOrderDetailNode | null;
}

/* ── Mutation ── */

const UPDATE_SERVICE_ORDER = `
  mutation UpdateServiceOrder(
    $accessKey: Float!
    $status: String
    $notes: String
  ) {
    updateServiceOrder(
      accessKey: $accessKey
      status: $status
      notes: $notes
    ) {
      accessKey
      status
      notes
    }
  }
`;

/* ── Status helpers ── */

function normaliseStatus(raw: string): OrderStatus {
  const lower = raw.toLowerCase();
  if (lower === 'in-progress' || lower === 'inprogress' || lower === 'in progress') {
    return 'in-progress';
  }
  if (lower === 'completed' || lower === 'complete') {
    return 'completed';
  }
  if (lower === 'cancelled' || lower === 'canceled') {
    return 'cancelled';
  }
  return 'pending';
}

type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

const statusBadgeVariant: Record<OrderStatus, GlassBadgeVariant> = {
  pending: 'warning',
  'in-progress': 'info',
  completed: 'success',
  cancelled: 'danger',
};

const statusLabel: Record<OrderStatus, string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const statusOptions: readonly { value: string; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/* ── Sub-components ── */

interface DetailFieldProps {
  label: string;
  value: string;
  icon?: ReactElement;
}

function DetailField({ label, value, icon }: DetailFieldProps): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-1.5 text-sm text-text">
        {icon !== undefined && <span className="text-text-tertiary shrink-0">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}

interface NotFoundProps {
  id: string;
  onBack: () => void;
}

function NotFound({ id, onBack }: NotFoundProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <ClipboardList size={48} className="text-text-tertiary" />
      <div className="text-center">
        <p className="text-lg font-semibold text-text">Order not found</p>
        <p className="text-sm text-text-secondary mt-1">
          No service order with ID &ldquo;{id}&rdquo; could be located.
        </p>
      </div>
      <GlassButton variant="secondary" size="md" onClick={onBack}>
        <ArrowLeft size={14} />
        Back to Orders
      </GlassButton>
    </div>
  );
}

/* ── Page ── */

export function ServiceOrderDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const resolvedId = id ?? '';
  const accessKey = parseFloat(resolvedId);

  const { data, loading, error, refetch } = useGraphQL<ServiceOrderData>(GET_SERVICE_ORDER, {
    accessKey,
  });

  const { mutate: updateOrder, loading: saving } = useGraphQLMutation(UPDATE_SERVICE_ORDER);

  const [editStatus, setEditStatus] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const hasChanges = editStatus !== null || editNotes !== null;

  function handleSave(): void {
    const variables: Record<string, unknown> = { accessKey };
    if (editStatus !== null) {
      variables['status'] = editStatus;
    }
    if (editNotes !== null) {
      variables['notes'] = editNotes;
    }
    void updateOrder(variables).then((result) => {
      if (result !== null) {
        setEditStatus(null);
        setEditNotes(null);
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
        refetch();
      }
    });
  }

  function handleBack(): void {
    void navigate('/');
  }

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader2 size={40} className="text-text-tertiary animate-spin" />
        <p className="text-sm text-text-secondary">Loading service order&hellip;</p>
      </div>
    );
  }

  /* ── Error state ── */
  if (error !== null) {
    return (
      <div className="space-y-6">
        <div>
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={handleBack}
            aria-label="Back to orders"
            className="-ml-1"
          >
            <ArrowLeft size={14} />
            My Service Orders
          </GlassButton>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load service order</p>
            <p className="text-xs mt-0.5 text-danger/80">{error}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      </div>
    );
  }

  /* ── Not found ── */
  if (!data?.serviceOrder) {
    return <NotFound id={resolvedId} onBack={handleBack} />;
  }

  const node = data.serviceOrder;
  const status = normaliseStatus(node.status);

  /* Build derived display values */
  const fullAddress =
    node.client !== null
      ? [node.client.address, node.client.city, node.client.state, node.client.zip]
          .filter(Boolean)
          .join(', ')
      : '';

  const completedCount = node.units.filter((u) => u.serviceComplete).length;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <div>
        <GlassButton
          variant="ghost"
          size="sm"
          onClick={handleBack}
          aria-label="Back to orders"
          className="-ml-1"
        >
          <ArrowLeft size={14} />
          My Service Orders
        </GlassButton>
      </div>

      {/* Save bar — shown when changes are pending */}
      {(hasChanges || saveSuccess) && (
        <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          {saveSuccess ? (
            <p className="text-sm font-medium text-success-fg">Changes saved successfully.</p>
          ) : (
            <p className="text-sm text-text-secondary">You have unsaved changes.</p>
          )}
          {hasChanges && (
            <GlassButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              <Save size={13} />
              {saving ? 'Saving\u2026' : 'Save Changes'}
            </GlassButton>
          )}
        </div>
      )}

      {/* Order header */}
      <GlassCard>
        <GlassCardContent className="py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-semibold text-text-secondary">
                  {node.ticketno}
                </span>
                <GlassBadge variant={statusBadgeVariant[status]}>{statusLabel[status]}</GlassBadge>
              </div>
              <h1 className="text-xl font-semibold text-text mt-1">{node.description}</h1>
              {node.notes !== null && node.notes.trim() !== '' && editNotes === null && (
                <p className="text-sm text-text-secondary leading-relaxed mt-0.5">{node.notes}</p>
              )}
            </div>
            <div className="flex flex-col gap-1 sm:items-end text-sm text-text-secondary shrink-0">
              {node.dueDate !== null && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={13} className="shrink-0" />
                  <span>Scheduled: {node.dueDate}</span>
                </div>
              )}
              {node.completedDate !== null && (
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="shrink-0" />
                  <span>Completed: {node.completedDate}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Clock size={13} className="shrink-0" />
                <span>Created: {node.createdDate}</span>
              </div>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Service Details */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-2">
            <Wrench size={16} className="text-text-secondary" />
            <h2 className="text-base font-semibold text-text">Service Details</h2>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Description" value={node.description} icon={<Wrench size={13} />} />
            {node.dueDate !== null && (
              <DetailField
                label="Scheduled Date"
                value={node.dueDate}
                icon={<Calendar size={13} />}
              />
            )}
            {node.completedDate !== null && (
              <DetailField
                label="Completed Date"
                value={node.completedDate}
                icon={<Clock size={13} />}
              />
            )}
            <DetailField
              label="Assigned Technician"
              value={node.assignedTo}
              icon={<User size={13} />}
            />
            <DetailField label="Priority" value={node.priority} />
            <DetailField label="Status" value={statusLabel[status]} />
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Update Status & Notes */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-text-secondary" />
            <h2 className="text-base font-semibold text-text">Update Order</h2>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <GlassSelect
              label="Update Status"
              options={statusOptions}
              value={editStatus ?? node.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                setEditStatus(newStatus === node.status ? null : newStatus);
              }}
            />
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-text-secondary block mb-1.5">Notes</label>
              <textarea
                value={editNotes ?? node.notes ?? ''}
                onChange={(e) => {
                  const newNotes = e.target.value;
                  setEditNotes(newNotes === (node.notes ?? '') ? null : newNotes);
                }}
                rows={3}
                placeholder="Add notes or comments about this order\u2026"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
              />
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Location */}
      {node.client !== null && (
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-text-secondary" />
              <h2 className="text-base font-semibold text-text">Location</h2>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <DetailField
                label="Client"
                value={node.client.clientName}
                icon={<Building2 size={13} />}
              />
              {fullAddress !== '' && (
                <DetailField label="Address" value={fullAddress} icon={<MapPin size={13} />} />
              )}
              {node.client.contact !== null && (
                <DetailField
                  label="Site Contact"
                  value={node.client.contact}
                  icon={<User size={13} />}
                />
              )}
              {node.client.phone !== null && (
                <DetailField
                  label="Contact Phone"
                  value={node.client.phone}
                  icon={<Phone size={13} />}
                />
              )}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Units */}
      <GlassCard>
        <GlassCardHeader>
          <h2 className="text-base font-semibold text-text">
            Units
            <span className="ml-2 text-sm font-normal text-text-secondary">
              ({String(completedCount)} of {String(node.units.length)} complete)
            </span>
          </h2>
        </GlassCardHeader>
        {node.units.length === 0 ? (
          <GlassCardContent>
            <p className="text-sm text-text-secondary text-center py-4">
              No units are associated with this order.
            </p>
          </GlassCardContent>
        ) : (
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell className="whitespace-nowrap">Unit</GlassTableHeaderCell>
                <GlassTableHeaderCell className="whitespace-nowrap hidden sm:table-cell">
                  Floor
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="text-center whitespace-nowrap">
                  Status
                </GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {node.units.map((unit) => (
                <GlassTableRow key={String(unit.accessKey)}>
                  <GlassTableCell>
                    <span className="font-mono text-xs font-semibold text-primary">
                      {unit.unitNo}
                    </span>
                    {unit.floor !== null && (
                      <p className="text-xs text-text-tertiary mt-0.5 sm:hidden">{unit.floor}</p>
                    )}
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary hidden sm:table-cell">
                    {unit.floor ?? '\u2014'}
                  </GlassTableCell>
                  <GlassTableCell className="text-center">
                    {unit.accessDenied ? (
                      <GlassBadge variant="danger">Access Denied</GlassBadge>
                    ) : unit.serviceComplete ? (
                      <CheckCircle2
                        size={18}
                        className="text-success inline-block"
                        aria-label="Completed"
                      />
                    ) : (
                      <XCircle
                        size={18}
                        className="text-text-tertiary inline-block"
                        aria-label="Not completed"
                      />
                    )}
                  </GlassTableCell>
                </GlassTableRow>
              ))}
            </GlassTableBody>
          </GlassTable>
        )}
      </GlassCard>

      {/* Service History */}
      {node.timeRecords.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-text-secondary" />
              <h2 className="text-base font-semibold text-text">
                Service History
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  ({String(node.timeRecords.length)} record
                  {node.timeRecords.length !== 1 ? 's' : ''})
                </span>
              </h2>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="flex flex-col gap-4">
              {node.timeRecords.map((record) => {
                const location = record.timeLocation;
                const clockIn =
                  location !== null && location.clockInTime !== null
                    ? new Date(location.clockInTime)
                    : null;
                const clockOut =
                  location !== null && location.clockOutTime !== null
                    ? new Date(location.clockOutTime)
                    : null;

                const formatTime = (d: Date | null): string =>
                  d !== null ? d.toLocaleString() : '\u2014';

                const formatDuration = (start: Date | null, stop: Date | null): string => {
                  if (start === null) {
                    return '\u2014';
                  }
                  const end = stop ?? new Date();
                  const ms = end.getTime() - start.getTime();
                  const h = Math.floor(ms / 3600000);
                  const m = Math.floor((ms % 3600000) / 60000);
                  return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m`;
                };

                const formatCoords = (lat: number | null, lng: number | null): string => {
                  if (lat === null || lng === null) {
                    return '\u2014';
                  }
                  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                };

                const clockInLocation = [
                  location?.clockInAddress,
                  location?.clockInCity,
                  location?.clockInState,
                ]
                  .filter((v): v is string => v !== null)
                  .join(', ');

                const gpsCoords =
                  location !== null
                    ? formatCoords(location.clockInLatitude, location.clockInLongitude)
                    : '\u2014';

                return (
                  <div
                    key={String(record.id)}
                    className="flex flex-col gap-2 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <GlassBadge
                          variant={
                            record.status === 'active'
                              ? 'success'
                              : record.status === 'paused'
                                ? 'warning'
                                : 'default'
                          }
                        >
                          {record.status ?? 'recorded'}
                        </GlassBadge>
                        {record.notes !== null && (
                          <span className="text-xs text-text-secondary">{record.notes}</span>
                        )}
                      </div>
                      <span className="text-xs font-medium text-text-secondary font-mono">
                        {formatDuration(clockIn, clockOut)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
                      <DetailField
                        label="Clock In"
                        value={formatTime(clockIn)}
                        icon={<Clock size={12} />}
                      />
                      <DetailField
                        label="Clock Out"
                        value={formatTime(clockOut)}
                        icon={<Clock size={12} />}
                      />
                      {clockInLocation !== '' && (
                        <DetailField
                          label="Clock In Location"
                          value={clockInLocation}
                          icon={<MapPin size={12} />}
                        />
                      )}
                      {record.cummulative !== null && (
                        <DetailField
                          label="Cumulative Time"
                          value={`${record.cummulative.toFixed(1)} min`}
                          icon={<Clock size={12} />}
                        />
                      )}
                      {location !== null && gpsCoords !== '\u2014' && (
                        <DetailField
                          label="Clock In GPS"
                          value={gpsCoords}
                          icon={<MapPin size={12} />}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Image Gallery */}
      {node.images.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-2">
              <Camera size={16} className="text-text-secondary" />
              <h2 className="text-base font-semibold text-text">
                Image Gallery
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  ({String(node.images.length)} photo{node.images.length !== 1 ? 's' : ''})
                </span>
              </h2>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {node.images.map((img) => {
                const src =
                  img.thumbnailUrl ?? (img.cfImageId !== null ? `/images/${img.cfImageId}` : null);
                const uploadedAt = new Date(img.uploadedDate);
                const sizeKB = img.fileSize !== null ? Math.round(img.fileSize / 1024) : null;
                const sizeLabel =
                  sizeKB !== null
                    ? sizeKB >= 1024
                      ? `${(sizeKB / 1024).toFixed(1)} MB`
                      : `${String(sizeKB)} KB`
                    : null;

                return (
                  <div
                    key={String(img.id)}
                    className="group relative flex flex-col gap-1.5 rounded-lg border border-[var(--glass-border)] overflow-hidden bg-[var(--glass-bg)]"
                  >
                    {src !== null ? (
                      <img
                        src={src}
                        alt={img.filename}
                        className="w-full aspect-square object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-bg-muted">
                        <ImageIcon size={24} className="text-text-tertiary" />
                      </div>
                    )}
                    <div className="px-2 pb-2">
                      <p className="text-xs font-medium text-text truncate" title={img.filename}>
                        {img.filename}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {uploadedAt.toLocaleDateString()}
                        {sizeLabel !== null && ` \u00b7 ${sizeLabel}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}
    </div>
  );
}
